import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import { UUID } from "@goose/utils/uuid";

import { AsyncTaskLabel } from "~/components/admin/AsyncTaskLabel";
import { AsyncTaskStatusBadge } from "~/components/admin/AsyncTaskStatusBadge";
import { Breadcrumbs } from "~/components/admin/Breadcrumbs";
import { DetailsTable } from "~/components/admin/DetailsTable";
import { AsyncTaskIcon } from "~/components/admin/Icons";
import { PageHeader } from "~/components/admin/PageHeader";
import { Surface } from "~/components/admin/Surface";
import { ErrorDisplay } from "~/components/ErrorDisplay";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate, formatDuration } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/aviary/tasks/$taskId",
)({
  component: TaskPage,
  params: {
    parse: ({ taskId }) => {
      return { taskId: UUID.from(taskId) };
    },
    stringify: ({ taskId }) => {
      return { taskId: taskId.toShort() };
    },
  },
  beforeLoad: ({ params, location }) => {
    // Canonicalize long-form UUID URLs to the Base58 short form.
    if (!location.pathname.endsWith(params.taskId.toShort())) {
      throw redirect({
        to: "/admin/aviary/tasks/$taskId",
        params,
        replace: true,
      });
    }
  },
});

function getLogUrl(taskId: string) {
  const url = "https://console.cloud.google.com/logs/query";
  const project = encodeURIComponent(process.env.GOOGLE_CLOUD_PROJECT);
  const query = encodeURIComponent(`"${taskId}"`);
  return `${url};query=${query}?project=${project}`;
}

function JsonSection({ title, value }: { title: string; value: unknown }) {
  return (
    <Surface as="section" className="overflow-hidden">
      <Surface.Header>
        <Surface.HeaderTitle className="text-lg">{title}</Surface.HeaderTitle>
      </Surface.Header>
      <pre className="overflow-x-auto px-6 py-5 text-xs leading-5 text-slate-800">
        {JSON.stringify(value, null, 2)}
      </pre>
    </Surface>
  );
}

function TaskPage() {
  const trpc = useTRPC();
  const { taskId } = Route.useParams();

  const query = useQuery(
    trpc.aviary.tasks.get.queryOptions(
      { id: taskId.toString() },
      {
        refetchInterval: (q) => {
          const status = q.state.data?.status;
          return status === "queued" || status === "running" ? 5_000 : false;
        },
      },
    ),
  );

  return (
    <div className="space-y-6">
      <QueryState
        result={query}
        loading={<Spinner size="lg" />}
        error={(error) => <TRPCClientErrorDisplay error={error} />}
        empty={<ErrorDisplay message="Task not found" level="warning" />}
      >
        {(task) => (
          <>
            <PageHeader
              breadcrumbs={
                <Breadcrumbs>
                  <Link
                    to="/admin"
                    className="transition-colors hover:text-slate-700"
                  >
                    Admin
                  </Link>
                  <Link
                    to="/admin/aviary/tasks"
                    className="transition-colors hover:text-slate-700"
                  >
                    Aviary Tasks
                  </Link>
                  <span className="text-slate-700">{task.id.slice(0, 8)}</span>
                </Breadcrumbs>
              }
              icon={AsyncTaskIcon}
              title={`Task ${task.id.slice(0, 8)}`}
              actions={
                <Button
                  as="a"
                  size="sm"
                  href={getLogUrl(task.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View logs
                </Button>
              }
            />

            <DetailsTable
              details={[
                {
                  label: "ID",
                  value: <span className="font-mono text-xs">{task.id}</span>,
                },
                {
                  label: "Status",
                  value: <AsyncTaskStatusBadge status={task.status} />,
                },
                {
                  label: "Resource",
                  value: task.resource,
                },
                {
                  label: "Resource ID",
                  value:
                    task.resourceId != null ? (
                      <span className="font-mono text-xs">
                        {task.resourceId}
                      </span>
                    ) : (
                      "—"
                    ),
                },
                {
                  label: "Operation",
                  value: task.operation,
                },
                {
                  label: "Labels",
                  value:
                    task.labels.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {task.labels.map((label) => (
                          <AsyncTaskLabel key={label} label={label} />
                        ))}
                      </div>
                    ) : (
                      "—"
                    ),
                },
                {
                  label: "Created",
                  value: formatDate(task.createdAt),
                },
                {
                  label: "Updated",
                  value: formatDate(task.updatedAt),
                },
                {
                  label: "Completed",
                  value: formatDate(task.completedAt),
                },
                {
                  label: "Duration",
                  value:
                    task.completedAt != null
                      ? formatDuration(
                          new Date(task.completedAt).getTime() -
                            new Date(task.createdAt).getTime(),
                        )
                      : "—",
                },
              ]}
            />

            {task.error != null && (
              <JsonSection title="Error" value={task.error} />
            )}
            {task.result != null && (
              <JsonSection title="Result" value={task.result} />
            )}
          </>
        )}
      </QueryState>
    </div>
  );
}
